import { useState, useEffect } from "react";
import { newDemande } from "../controllers/controller.demand";
import { toast } from "react-toastify";
import { Get } from "../services/service.api";


// The form serves two requests: a doctor joining the network, or a cabinet asking for an
// ADMIN account (the person filling it in runs the cabinet on Medic Admin, the web app).
const OTHER = '__other__';
const TYPES = [
    { id: 'MEDECIN', label: 'Je suis médecin' },
    { id: 'CABINET', label: 'Je représente un cabinet' },
];
const CABINET_HASH = '#join-cabinet';
const FALLBACK_LOCALITES = [
    { pays: 'Burkina Faso', villes: ['Banfora', 'Bobo-Dioulasso', 'Koudougou', 'Ouagadougou', 'Ouahigouya'] },
];

export default function ViewContact() {

    const [type, setType] = useState(() => window.location.hash === CABINET_HASH ? 'CABINET' : 'MEDECIN');
    const isCabinet = type === 'CABINET';
    const [demande, setDemande] = useState({
        'nom': '', 'prenom': '', 'email': '', 'tel': '', 'ordre': '', 'pays': '', 'ville': '',
        'adresse': '', 'prix_consultation_min': '', 'prix_consultation_max': '', 'cabinet': '', 'cabinet_adresse': '',
        'specialite': ''
    });
    const [selectedCountry, setSelectedCountry] = useState('');
    const [cabinets, setCabinets] = useState([]);
    const [isNewCabinet, setIsNewCabinet] = useState(true);
    const [specialites, setSpecialites] = useState([]);
    const [otherSpecialite, setOtherSpecialite] = useState(false);
    // countries and cities managed by the Medic'help team (public list); the fallback keeps the form usable offline
    const [localites, setLocalites] = useState(FALLBACK_LOCALITES);
    const [attestationFile, setAttestation] = useState(null);
    const [fileError, setFileError] = useState('');

    useEffect(() => {
        async function fetchCabinets() {
            try {
                const res = await Get("/cabinets/all");
                if (res && res.cabinets) {
                    setCabinets(res.cabinets);
                }
            } catch (error) {
                console.error("Erreur lors de la récupération des cabinets:", error);
            }
        }
        async function fetchSpecialites() {
            try {
                const res = await Get("/specialites/all");
                if (res && res.specialites) {
                    setSpecialites(res.specialites);
                }
            } catch (error) {
                console.error("Erreur lors de la récupération des spécialités:", error);
            }
        }
        async function fetchLocalites() {
            try {
                const res = await Get("/localites/all");
                const list = (res?.localites || [])
                    .filter((l) => l?.pays && Array.isArray(l.villes) && l.villes.length)
                    .map((l) => ({ pays: l.pays, villes: [...l.villes].sort((a, b) => a.localeCompare(b, 'fr')) }))
                    .sort((a, b) => a.pays.localeCompare(b.pays, 'fr'));
                if (list.length) setLocalites(list);
            } catch (error) {
                console.error("Erreur lors de la récupération des localités:", error);
            }
        }
        fetchCabinets();
        fetchSpecialites();
        fetchLocalites();
    }, []);

    useEffect(() => {
        const onHash = () => { if (window.location.hash === CABINET_HASH) setType('CABINET'); };
        window.addEventListener('hashchange', onHash);
        return () => window.removeEventListener('hashchange', onHash);
    }, []);

    const handleFormChange = (field, value) => {
        if (field === 'pays') {
            setSelectedCountry(value);
        }

        setDemande(prev => ({
            ...prev,
            [field]: value
        }));
        
    };



    const handleAttestationChange = (e) => {
        const file = e.target.files[0];
        setFileError('');
        if (!file) {
            setAttestation(null);
            return;
        }
        const ALLOWED = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
        const MAX_SIZE = 5 * 1024 * 1024; // 5 Mo
        if (!ALLOWED.includes(file.type)) {
            setFileError('Format non accepté. Veuillez uploader un PDF, JPG ou PNG.');
            setAttestation(null);
            return;
        }
        if (file.size > MAX_SIZE) {
            setFileError('Le fichier dépasse 5 Mo. Veuillez compresser votre document.');
            setAttestation(null);
            return;
        }
        setAttestation(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // Envoyer la demande au serveur
        const missing = isCabinet
            ? !demande.nom || !demande.prenom || !demande.tel || !demande.pays || !demande.ville || !demande.cabinet.trim() || !demande.cabinet_adresse.trim()
            : !demande.nom || !demande.prenom || !demande.tel || !demande.ordre || !demande.pays || !demande.ville || !demande.prix_consultation_min || !demande.prix_consultation_max || !demande.specialite.trim();
        if(missing){
            toast.warning("Veuillez remplir tous les champs obligatoires.");
            return;
        }

        const cleanedPhone = demande.tel.replace(/\s+/g, '');
        const phoneRegex = /^\+[1-9][0-9]{7,14}$/;
        if (!phoneRegex.test(cleanedPhone)) {
            toast.warning("Le numéro de téléphone doit commencer par l'indicatif (ex: +226, +33) suivi de vos chiffres.");
            return;
        }

        // the cabinet proof is optional for now; the doctor's attestation is not
        if (!attestationFile && !isCabinet) {
            toast.warning("Veuillez joindre votre attestation d'inscription à l'ordre des médecins.");
            return;
        }
        const payload = isCabinet
            ? { ...demande, type, ordre: '', specialite: '', adresse: '', prix_consultation_min: '', prix_consultation_max: '' }
            : { ...demande, type, specialite: demande.specialite.replace(/\s+/g, ' ').trim() };
        await newDemande(payload, attestationFile);
    }

    return (
        <section id="join" className="py-24 bg-[#f7f6fd]">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-12">
                    <p className="text-sm font-bold tracking-wider text-brand-600 uppercase mb-3">Espace professionnels</p>
                    <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-950 mb-4 tracking-tight">Rejoignez le réseau Medic'help</h2>
                    <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
                        {isCabinet
                            ? "Vous gérez un cabinet ? Demandez votre espace pour suivre vos médecins, vos rendez-vous et vos créneaux."
                            : "Vous êtes médecin ? Inscrivez-vous pour rejoindre notre réseau et digitaliser votre pratique."}
                    </p>
                </div>
                
                <form className="bg-white rounded-[2rem] shadow-[0_30px_60px_-30px_rgba(26,18,71,0.25)] p-6 sm:p-10 md:p-12 border border-gray-100 transition-all duration-300 ">
                    <div id="join-cabinet" role="radiogroup" aria-label="Type de demande" className="grid grid-cols-2 gap-1 p-1 rounded-2xl bg-gray-100 mb-8 scroll-mt-28">
                        {TYPES.map((t) => (
                            <button key={t.id} type="button" role="radio" aria-checked={type === t.id} onClick={() => setType(t.id)}
                                className={`min-h-[48px] px-3 py-2 rounded-xl text-sm sm:text-base font-bold transition-colors ${type === t.id ? 'bg-white text-brand-700 shadow-sm' : 'text-gray-500 hover:text-brand-700'}`}>
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {isCabinet && (
                        <p className="mb-8 p-4 rounded-xl bg-brand-50 text-sm text-brand-900">
                            Remplissez ce formulaire avec les coordonnées de la personne qui gérera le cabinet. Après vérification, elle recevra un lien pour activer son compte sur <b>Medic Admin</b>, le site de gestion : médecins du cabinet, rendez-vous, créneaux. Ce compte ne donne pas accès à l'application mobile.
                        </p>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8">
                        <div className="group">
                            <label className="block text-sm text-gray-600 font-semibold mb-2 group-focus-within:text-brand-600 transition-colors">Prénom *</label>
                            <input onChange={(e) => handleFormChange('prenom', e.target.value)} type="text" name="firstName" autoComplete="given-name" required className="w-full px-5 py-3.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10 outline-none transition-all duration-300" placeholder="Ex: Jean" />
                        </div>
                        
                        <div className="group">
                            <label className="block text-sm text-gray-600 font-semibold mb-2 group-focus-within:text-brand-600 transition-colors">Nom *</label>
                            <input onChange={(e) => handleFormChange('nom', e.target.value)} type="text" name="lastName" autoComplete="family-name" required className="w-full px-5 py-3.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10 outline-none transition-all duration-300" placeholder="Ex: Dupont" />
                        </div>
                        
                        <div className="group">
                            <label className="block text-sm text-gray-600 font-semibold mb-2 group-focus-within:text-brand-600 transition-colors">Téléphone *</label>
                            <input onChange={(e) => handleFormChange('tel', e.target.value)} type="tel" name="phone" autoComplete="tel" required className="w-full px-5 py-3.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10 outline-none transition-all duration-300" placeholder="+226 XX XX XX XX" />
                        </div>
                        
                        {!isCabinet && <div className="group">
                            <label className="block text-sm text-gray-600 font-semibold mb-2 group-focus-within:text-brand-600 transition-colors">Numéro ordre de médecin *</label>
                            <input onChange={(e) => handleFormChange('ordre', e.target.value)} type="text" name="doctorNumber" required className="w-full px-5 py-3.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10 outline-none transition-all duration-300" placeholder="Ex: ONM-12345" />
                        </div>}
                        
                        <div className="group">
                            <label className="block text-sm text-gray-600 font-semibold mb-2 group-focus-within:text-brand-600 transition-colors">Email</label>
                            <input onChange={(e) => handleFormChange('email', e.target.value)} type="email" name="email" autoComplete="email" required className="w-full px-5 py-3.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10 outline-none transition-all duration-300" placeholder="jean.dupont@email.com" />
                        </div>
                        
                        {!isCabinet && <div className="group">
                            <label className="block text-sm text-gray-600 font-semibold mb-2 group-focus-within:text-brand-600 transition-colors">Adresse *</label>
                            <input onChange={(e) => handleFormChange('adresse', e.target.value)} type="text" name="address" autoComplete="street-address" required className="w-full px-5 py-3.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10 outline-none transition-all duration-300" placeholder="Ex: Secteur 15, Ouagadougou" />
                        </div>}
                        
                        <div className="group">
                            <label className="block text-sm text-gray-600 font-semibold mb-2 group-focus-within:text-brand-600 transition-colors">Pays *</label>
                            <div className="relative">
                                <select value={demande.pays} onChange={(e) => { handleFormChange('pays', e.target.value); handleFormChange('ville', ''); }} name="country" required className="appearance-none w-full px-5 py-3.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10 outline-none transition-all duration-300 cursor-pointer">
                                    <option value="">Sélectionner un pays</option>
                                    {localites.map((loc, index) => (
                                        <option key={index} value={loc.pays}>{loc.pays}</option>
                                    ))}
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
                                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                                </div>
                            </div>
                        </div>

                        <div className="group">
                            <label className="block text-sm text-gray-600 font-semibold mb-2 group-focus-within:text-brand-600 transition-colors">Ville *</label>
                            <div className="relative">
                                <select value={demande.ville} disabled={!selectedCountry} onChange={(e) => handleFormChange('ville', e.target.value)} name="city" required className="disabled:cursor-not-allowed disabled:opacity-60 appearance-none w-full px-5 py-3.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10 outline-none transition-all duration-300 cursor-pointer">
                                    <option value="">{selectedCountry ? 'Sélectionner une ville' : "Choisissez d'abord le pays"}</option>
                                    {localites.filter((loc) => loc.pays === selectedCountry).flatMap((loc) => 
                                        loc.villes.map((ville, index) => (
                                            <option key={loc.pays + index} value={ville}>{ville}</option>
                                        ))
                                    )}
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
                                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                                </div>
                            </div>
                        </div>
                        
                        {!isCabinet && <>
                        <div className="group">
                            <label className="block text-sm text-gray-600 font-semibold mb-2 group-focus-within:text-brand-600 transition-colors">Spécialité *</label>
                            <div className="relative">
                                <select value={otherSpecialite ? OTHER : demande.specialite} onChange={(e) => {
                                    const other = e.target.value === OTHER;
                                    setOtherSpecialite(other);
                                    handleFormChange('specialite', other ? '' : e.target.value);
                                }} name="specialte" required className="appearance-none w-full px-5 py-3.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10 outline-none transition-all duration-300 cursor-pointer">
                                    <option value="">Sélectionner une spécialité</option>
                                    {specialites.map((spec) => (
                                        <option key={spec._id} value={spec.libelle}>
                                            {spec.libelle}
                                        </option>
                                    ))}
                                    <option value={OTHER}>Autre (ma spécialité n'est pas dans la liste)</option>
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
                                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                                </div>
                            </div>
                        </div>
                        
                        {otherSpecialite && (
                        <div className="group">
                            <label className="block text-sm text-gray-600 font-semibold mb-2 group-focus-within:text-brand-600 transition-colors">Votre spécialité *</label>
                            <input value={demande.specialite} onChange={(e) => handleFormChange('specialite', e.target.value)} type="text" maxLength={80} required className="w-full px-5 py-3.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10 outline-none transition-all duration-300" placeholder="Ex : Médecine du sport" />
                            <p className="mt-1.5 text-xs text-gray-500">Notre équipe l'ajoutera à la liste en validant votre candidature.</p>
                        </div>
                        )}

                        <div className="group">
                            <label className="block text-sm text-gray-600 font-semibold mb-2 group-focus-within:text-brand-600 transition-colors">Prix de consultation (XOF) *</label>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <input onChange={(e) => handleFormChange('prix_consultation_min', e.target.value)} type="number" name="price_min" inputMode="numeric" required min="0" step="1000" className="w-full px-5 py-3.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10 outline-none transition-all duration-300" placeholder="Min (Ex: 10000)" />
                                </div>
                                <div>
                                    <input onChange={(e) => handleFormChange('prix_consultation_max', e.target.value)} type="number" name="price_max" inputMode="numeric" required min="0" step="1000" className="w-full px-5 py-3.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10 outline-none transition-all duration-300" placeholder="Max (Ex: 20000)" />
                                </div>
                            </div>
                        </div>
                        </>}

                        <div className="md:col-span-2 group">
                            <label className="block text-sm text-gray-600 font-semibold mb-2 group-focus-within:text-brand-600 transition-colors">{isCabinet ? "Votre cabinet est-il déjà sur Medic'help ?" : "Cabinet existant ?"}</label>
                            <div className="relative">
                                <select 
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === "new") {
                                            setIsNewCabinet(true);
                                            setDemande(prev => ({ ...prev, cabinet: "", cabinet_adresse: "" }));
                                        } else {
                                            setIsNewCabinet(false);
                                            const selectedCab = cabinets.find(c => c._id === val);
                                            if (selectedCab) {
                                                setDemande(prev => ({ 
                                                    ...prev, 
                                                    cabinet: selectedCab.libelle, 
                                                    cabinet_adresse: selectedCab.adresse 
                                                }));
                                            }
                                        }
                                    }} 
                                    className="appearance-none w-full px-5 py-3.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10 outline-none transition-all duration-300 cursor-pointer"
                                >
                                    <option value="new">{isCabinet ? "-- Non, c'est un nouveau cabinet --" : "-- Je crée un nouveau cabinet --"}</option>
                                    {cabinets.map((cab) => (
                                        <option key={cab._id} value={cab._id}>
                                            {cab.libelle} {cab.adresse ? `(${cab.adresse})` : ''}
                                        </option>
                                    ))}
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
                                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                                </div>
                            </div>
                        </div>
                        
                        <div className="md:col-span-2 group">
                            <label className="block text-sm text-gray-600 font-semibold mb-2 group-focus-within:text-brand-600 transition-colors">Nom du cabinet{isCabinet ? ' *' : ''}</label>
                            <input 
                                value={demande.cabinet}
                                disabled={!isNewCabinet}
                                onChange={(e) => handleFormChange('cabinet', e.target.value)} 
                                type="text" 
                                name="companyName" 
                                placeholder="Ex: Clinique Pasteur"
                                className="w-full px-5 py-3.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10 outline-none transition-all duration-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-100 disabled:cursor-not-allowed" 
                            />
                        </div>

                        <div className="md:col-span-2 group">
                            <label className="block text-sm text-gray-600 font-semibold mb-2 group-focus-within:text-brand-600 transition-colors">Adresse du cabinet{isCabinet ? ' *' : ''}</label>
                            <input 
                                value={demande.cabinet_adresse}
                                disabled={!isNewCabinet}
                                onChange={(e) => handleFormChange('cabinet_adresse', e.target.value)} 
                                type="text" 
                                name="companyAddress" 
                                placeholder="Ex: Rue de la Clinique, Ouagadougou"
                                className="w-full px-5 py-3.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10 outline-none transition-all duration-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-100 disabled:cursor-not-allowed" 
                            />
                        </div>
                        
                        <div className="md:col-span-2">
                            <label className="block text-sm text-gray-600 font-semibold mb-2">
                                {isCabinet ? <>Justificatif du cabinet (autorisation d'ouverture ou agrément) <span className="font-normal text-gray-400">(facultatif)</span></> : "Attestation d'inscription à l'ordre des médecins *"}
                            </label>
                            <div className="w-full px-6 py-8 rounded-2xl border-2 border-dashed border-brand-300 bg-brand-50/50 hover:bg-brand-50 text-center cursor-pointer hover:border-brand-500 transition-all duration-300 relative group overflow-hidden">
                                <input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={handleAttestationChange}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                    required={!isCabinet}
                                />
                                {attestationFile ? (
                                    <div className="flex flex-col items-center justify-center gap-2 text-brand-700 font-medium">
                                        <div className="bg-brand-100 p-3 rounded-full mb-2">
                                            <span className="text-2xl">📄</span>
                                        </div>
                                        <span className="truncate max-w-xs font-semibold">{attestationFile.name}</span>
                                        <span className="text-green-600 font-bold bg-green-100 px-3 py-1 rounded-full text-xs">Fichier prêt ✓</span>
                                    </div>
                                ) : (
                                    <div className="text-gray-500 transform group-hover:scale-105 transition-transform duration-300">
                                        <div className="bg-white shadow-sm p-4 rounded-full inline-block mb-3">
                                            <span className="text-3xl">📁</span>
                                        </div>
                                        <p className="mt-1 text-base font-medium text-brand-900">Cliquez ou glissez pour sélectionner {isCabinet ? 'votre justificatif' : 'votre attestation'}</p>
                                        <p className="text-sm text-brand-600/70 mt-1 font-medium">(PDF, JPG ou PNG — max 5 Mo)</p>
                                    </div>
                                )}
                                <div className="absolute inset-0 bg-gradient-to-r from-brand-500/5 to-brand-500/5 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                            </div>
                            {fileError && (
                                <p className="mt-2 text-sm text-red-500 font-semibold animate-pulse">⚠ {fileError}</p>
                            )}
                            
                            <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/60 rounded-xl text-sm text-amber-900 shadow-sm">
                                <div className="flex items-start gap-3">
                                    <span className="text-xl">📌</span>
                                    <div>
                                        <p className="font-bold mb-1 text-amber-950">Vous n'avez pas encore ce document ?</p>
                                        <p className="text-amber-800/80 mb-2 font-medium">Contactez notre équipe SavanTech pour être guidé dans votre démarche :</p>
                                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 mt-2">
                                            <a href="mailto:savantech25@gmail.com" className="inline-flex items-center gap-2 bg-white/60 px-3 py-1.5 rounded-lg hover:bg-white transition-colors font-semibold text-amber-900">
                                                <span>📧</span> savantech25@gmail.com
                                            </a>
                                            <a href="https://wa.me/22604894780" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-white/60 px-3 py-1.5 rounded-lg hover:bg-white transition-colors font-semibold text-amber-900">
                                                <span>📱</span> +226 04 89 47 80 (WhatsApp)
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="mt-10">
                        <button onClick={handleSubmit} type="button" className="w-full bg-gradient-to-r from-brand-500 to-brand-700 hover:brightness-110 text-white py-4 px-8 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl hover:shadow-brand-500/30 transform hover:-translate-y-1 transition-all duration-300 z-10 flex justify-center items-center gap-2">
                            <span>{isCabinet ? "Demander l'espace de mon cabinet" : "Soumettre ma candidature"}</span>
                        </button>
                    </div>
                    
                    <div id="successMessage" className="hidden mt-6 p-4 bg-green-50 border-l-4 border-green-500 text-green-800 rounded-r-lg shadow-sm">
                        <div className="flex items-center gap-3 font-medium">
                            <span className="text-2xl">✅</span>
                            <p>Votre demande a été envoyée ! Notre équipe vous contactera très prochainement.</p>
                        </div>
                    </div>
                </form>
            </div>
        </section>
    )

}